import {
  ENABLE_8_PERC_SST,
  ENABLE_SERVICE_FEE,
  PLAN_BILL_CYCLE,
  PLAN_TYPE,
} from '@utils/constant';
import { useTranslation } from 'next-i18next';
import React from 'react';

import { useRouter } from 'next/router';
import { reformatPlanName } from 'pages/eligibility/takaful/plans';
import { PricingBreakdown } from 'typings/renewal';
import Button from '../Button';
import { calculateServiceFee } from '@utils/payments';

type ShowPolicySummaryProps = {
  pricingBreakdown: PricingBreakdown;
  billCycleSelected?: string | null;
  planType: PLAN_TYPE;
  onEditPlan?: (petId: string) => void;
  onEdit?: (petId: string) => void;
  showAddPet?: boolean;
  addPet?: () => void;
  showHeader?: boolean;
  showTotalPayable?: boolean;
  showSST?: boolean;
  showServiceFee?: boolean;
  showStampDuty?: boolean;
  showNCD?: boolean;
  serviceFee?: number;
  isTakafulProduct?: boolean;
};

const ShowPolicySummary: React.FC<ShowPolicySummaryProps> = ({
  pricingBreakdown,
  billCycleSelected,
  planType,
  onEdit,
  onEditPlan,
  showAddPet,
  addPet,
  showHeader = true, // NOTE: to support renewal display flow
  showTotalPayable = true, // NOTE: to support renewal display flow
  showSST = true, // NOTE: to support renewal display flow
  showServiceFee = true,
  showStampDuty = true, // NOTE: to support renewal display flow
  showNCD = true, // NOTE: to support renewal display flow
  serviceFee = 0.0,
  isTakafulProduct = false,
}: ShowPolicySummaryProps) => {
  const { t } = useTranslation('policySummary');
  const { t: tc } = useTranslation('common');
  const { t: tp } = useTranslation('planCard');
  const router = useRouter();
  const isRenew = planType === PLAN_TYPE.RENEWAL;
  const isTakaful = isTakafulProduct || router.asPath.includes('takaful');

  const totalPayable =
    billCycleSelected === PLAN_BILL_CYCLE.YEARLY
      ? pricingBreakdown.totalPayable + serviceFee
      : pricingBreakdown.firstMonthPayableWithTax + serviceFee;

  return (
    <section className="space-y-2">
      {showHeader && (
        <h3 className="text-lg font-semibold">{t('price_breakdown')}</h3>
      )}
      {pricingBreakdown?.policy?.map((policy, i) => {
        const {
          id,
          petName,
          plan,
          yearlyPrice,
          yearlyTotalLoadingAmount,
          monthlyPrice,
          monthlyLoading,
          monthlyDiscount,
          yearlyAmount,
          monthlyAmount,
          discountAmount,
        } = policy;

        //before discount
        const price =
          (billCycleSelected === PLAN_BILL_CYCLE.YEARLY
            ? yearlyPrice + yearlyTotalLoadingAmount
            : monthlyPrice + +monthlyLoading) || 0;

        const finalPrice =
          billCycleSelected === PLAN_BILL_CYCLE.YEARLY
            ? yearlyAmount
            : monthlyAmount - monthlyDiscount;

        let newFinalPrice = 0;

        if (pricingBreakdown.isMultiPet) {
          newFinalPrice =
            billCycleSelected === PLAN_BILL_CYCLE.YEARLY
              ? finalPrice - (pricingBreakdown.discountAmount ?? 0)
              : finalPrice;
        } else {
          newFinalPrice = finalPrice;
        }

        return (
          <React.Fragment key={String(i)}>
            <div className="flex justify-between">
              <div className="">
                <div className="font-semibold ">{petName}</div>
                <div
                  className={
                    '' + (isRenew ? 'text-gray-400' : 'text-primary-400')
                  }
                >
                  {isTakaful
                    ? tp(reformatPlanName(plan), {
                        petType: 'Kucing',
                      }) + ' Takaful'
                    : tp(reformatPlanName(plan), {
                        petType: plan.includes('Cat') ? tc('cat') : tc('dog'),
                      })}
                  {!isRenew && !isTakaful ? (
                    <span className="ml-1 ">
                      {billCycleSelected === PLAN_BILL_CYCLE.YEARLY
                        ? t('yearly_plan')
                        : t('monthly_plan')}
                    </span>
                  ) : null}
                </div>
                {!!onEdit && (
                  <div>
                    <Button asText className="link" onClick={() => onEdit(id)}>
                      {tp('edit_pet')}
                    </Button>
                  </div>
                )}
                {!!onEditPlan && (
                  <div>
                    <Button
                      asText
                      className="link"
                      onClick={() => onEditPlan(id)}
                    >
                      {t('edit_plan')}
                    </Button>
                  </div>
                )}
              </div>

              <div>
                {!!newFinalPrice && (
                  <p className="font-semibold">
                    RM
                    {newFinalPrice.toFixed(2)}/
                    {billCycleSelected === PLAN_BILL_CYCLE.YEARLY
                      ? t('year')
                      : t('month')}
                  </p>
                )}

                {!!discountAmount && (
                  <div className="line-through text-right">
                    RM
                    {price.toFixed(2)}
                  </div>
                )}
              </div>
            </div>

            <div className="flex">
              <div className="flex-grow">
                {!isRenew ? (
                  <div className=" text-gray-400">
                    {t('oyen_commission')}
                    (RM
                    {(newFinalPrice * 0.25).toFixed(2)})
                  </div>
                ) : (
                  ''
                )}
              </div>
            </div>
            <div className="w-full h-0.5 bg-gray-100" />
          </React.Fragment>
        );
      })}

      {showAddPet && (
        <div>
          <Button
            onClick={addPet}
            className="w-full rounded-full my-4"
            outlined
          >
            {tc('add_pet')}
          </Button>
        </div>
      )}

      {pricingBreakdown && pricingBreakdown.policy?.length > 0 && (
        <>
          {showNCD && isRenew && pricingBreakdown.totalNcd! > 0 && (
            <>
              <div className="flex justify-between font-semibold">
                <span>{`${t('ncd')} (${pricingBreakdown.ncdPercent}%)`}</span>
                <span>
                  -RM
                  {pricingBreakdown.totalNcd!.toFixed(2)}
                </span>
              </div>
              <div className="w-full h-0.5 bg-gray-100" />
            </>
          )}

          {showSST && (
            <>
              <div className="flex justify-between font-semibold">
                <span>{ENABLE_8_PERC_SST ? t('sst_8perc') : t('sst')}</span>
                <span>
                  RM
                  {billCycleSelected === PLAN_BILL_CYCLE.YEARLY &&
                    pricingBreakdown.totalTax.toFixed(2)}
                  {billCycleSelected === PLAN_BILL_CYCLE.MONTHLY &&
                    pricingBreakdown.monthlyTax.toFixed(2)}
                </span>
              </div>
              <div className="w-full h-0.5 bg-gray-100" />
            </>
          )}

          {showStampDuty && pricingBreakdown.stampDuty && (
            <>
              <div className="flex justify-between font-semibold">
                <div className="flex flex-col">
                  <span>{tp('stamp_duty')}</span>
                  {isRenew && (
                    <span className="text-xs font-light">
                      {t('one_time_payment')}
                    </span>
                  )}
                </div>
                <span>
                  RM
                  {pricingBreakdown.stampDuty.toFixed(2)}
                </span>
              </div>
              <div className="w-full h-0.5 bg-gray-100" />
            </>
          )}

          {showServiceFee && ENABLE_SERVICE_FEE && (
            <>
              <div className="flex justify-between font-semibold">
                <span>{t('service_fee')}</span>
                <span>RM{serviceFee.toFixed(2)}</span>
              </div>
              <div className="w-full h-0.5 bg-gray-100" />
            </>
          )}

          {showTotalPayable && (
            <>
              <div>
                <span className="flex justify-between">
                  <span className="flex justify-center">
                    {t('total_payable')}{' '}
                  </span>

                  <span>
                    <span className="font-bold text-xl">
                      RM {totalPayable.toFixed(2)}
                    </span>
                    <span className="font-light text-xs">
                      {billCycleSelected === PLAN_BILL_CYCLE.MONTHLY
                        ? t('first_month')
                        : isRenew
                        ? t('yearly')
                        : null}
                    </span>
                  </span>
                </span>
                {billCycleSelected === PLAN_BILL_CYCLE.MONTHLY && (
                  <span className="flex justify-end text-xs">
                    RM
                    {(
                      pricingBreakdown.monthlyPayable +
                      pricingBreakdown.monthlyTax +
                      calculateServiceFee(
                        pricingBreakdown.monthlyPayable,
                        pricingBreakdown.monthlyTax,
                        'Monthly',
                        false,
                        true
                      )
                    ).toFixed(2)}
                    {t('billed_monthly_subsequently')}
                  </span>
                )}
              </div>
              <div className="w-full h-0.5 bg-gray-100" />
            </>
          )}
        </>
      )}
    </section>
  );
};

export default ShowPolicySummary;
